import type { InviteContinuationContext } from '../hooks/useInviteContinuation';

/**
 * The invitation banner shown above the CANONICAL auth form.
 *
 * An invited person arrives at a page built for someone who already knows why
 * they are there. Without a word of context the page reads as a generic signup
 * wall and the invitation — the whole reason they clicked — is invisible. This
 * says who invited them, to what, and as what, so the form beneath it has an
 * obvious purpose.
 *
 * It is DECORATION, not authority. Every value here is display-only: the
 * workspace, the role and the address are bound to the invitation server-side
 * and are re-derived from its token at acceptance, so nothing shown (or
 * tampered with) here can change what the invitation grants. The inviter's
 * name arrives already masked — the recipient has no claim to it.
 */
export function InviteWelcome({ invite }: { invite: InviteContinuationContext }) {
  const workspace = invite.workspaceName || 'your new workspace';
  // A single letter reads as a mark rather than a truncation, and it never
  // needs a font that has the glyph for a whole name.
  const initial = (invite.workspaceName || '?').trim().charAt(0).toUpperCase();

  const expires = (() => {
    if (!invite.expiresAt) return '';
    const when = new Date(invite.expiresAt);
    if (Number.isNaN(when.getTime())) return '';
    const days = Math.ceil((when.getTime() - Date.now()) / 86_400_000);
    if (days < 0) return '';
    if (days === 0) return 'Expires today';
    return `Expires in ${days} day${days === 1 ? '' : 's'}`;
  })();

  return (
    <div className="mb-6 rounded-xl border border-[#25BD71]/25 bg-[#25BD71]/[0.06] p-4">
      <div className="flex items-start gap-3">
        <div
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#25BD71] text-base font-semibold text-white"
        >
          {initial}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#1EA35E]">
            You've been invited
          </p>
          <p className="mt-0.5 break-words text-[15px] font-semibold leading-snug text-gray-900">
            {workspace}
          </p>
          <p className="mt-1 text-[13px] leading-snug text-gray-600">
            {invite.inviterName ? <>Invited by {invite.inviterName}</> : 'Invitation'}
            {invite.role && (
              <>
                {' · '}
                <span className="capitalize">{invite.role}</span> access
              </>
            )}
          </p>
        </div>
      </div>
      {expires && (
        <p className="mt-3 border-t border-[#25BD71]/20 pt-2 text-[11px] text-gray-500">{expires}</p>
      )}
    </div>
  );
}
